import React, { useState, useRef, useEffect } from "react";
import { Marker, Polygon, useMap } from "react-leaflet";
import L from "leaflet";
import ConstantsImages from "../../../constants/ConstantsImages";
import { DivColumn, DivRow, ParagraphText } from "../../../styles/SharedStyles";
import WidgetIcon from "../../icons/widget-icon/WidgetIcon";
import UtilsUnit from "../../../utils/UtilsUnit";
import MapPolygonUtils from "../../../utils/MapPolygonUtils";
import ConstantsMap from "../../../constants/ConstantsMap";
import { wmResolution, wmZoomForResolution } from "../../../constants/MapCRS";
import WidgetMapLayout from "../widget-map-layout/WidgetMapLayout";
import { useWidgetMarkersMap } from "./useWidgetMarkersMap";
import "./WidgetMarkersMap.css";

function SelectedMarkerPopup({ selectedMarker, contentRef, navigate }) {
  const map = useMap();
  useEffect(() => {
    if (!selectedMarker || !map) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 20;
    const delayMs = 50;

    const attachPopupWhenReady = () => {
      if (cancelled) return;
      const ref = contentRef.current;
      if (!ref) return;

      const hasSvgIcon = !!ref.querySelector("svg");
      const shouldFallback = attempts >= maxAttempts;

      if (hasSvgIcon || shouldFallback) {
        const content = ref.cloneNode(true);
        content.style.display = "";
        content.addEventListener("click", (e) => e.stopPropagation());
        const navBtn = content.querySelector(".widget-markers-map-popup-nav-button");
        if (navBtn && navigate) {
          navBtn.addEventListener("click", () => {
            UtilsUnit.handleSearchFrameClick(navigate, selectedMarker);
          });
        }
        L.popup().setLatLng([selectedMarker.latitude, selectedMarker.longitude]).setContent(content).openOn(map);
        return;
      }

      attempts += 1;
      setTimeout(attachPopupWhenReady, delayMs);
    };

    attachPopupWhenReady();
    return () => {
      cancelled = true;
      map.closePopup();
    };
  }, [selectedMarker, map, contentRef, navigate]);
  return null;
}

function WidgetMarkersMap({
  centerProp,
  heightProp,
  widthProp,
  transitionProp,
  markers = [],
  hasActiveFilters = false,
  zoomCoordinates = null,
  showPinMarkers = true,
}) {
  const [mapType, setMapType] = useState("carto");
  const [mapState, setMapState] = useState(null);
  const popupContentRef = useRef(null);

  const { mapCenter, handleCloseMarkerClick, setMapRef, handleMarkerClick, selectedMarker, navigate, mapContainerRef } = useWidgetMarkersMap({
    centerProp,
    markers,
    hasActiveFilters,
    zoomCoordinates,
  });

  const [defaultLat, defaultLng] = ConstantsMap.DEFAULT_CENTER;
  const centerFromProp = Array.isArray(mapCenter) ? mapCenter : [mapCenter?.lat ?? defaultLat, mapCenter?.lng ?? defaultLng];
  const lat = mapState?.center?.lat ?? centerFromProp[0] ?? defaultLat;
  const lng = mapState?.center?.lng ?? centerFromProp[1] ?? defaultLng;
  const center = Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : ConstantsMap.DEFAULT_CENTER;
  const resolution = mapState?.resolution ?? wmResolution(ConstantsMap.DEFAULT_ZOOM, center[0]);
  const zoom = wmZoomForResolution(resolution, center[0]);

  const busIcon = L.icon({
    iconUrl: ConstantsImages.BUS_PARKING_IMAGE,
    iconSize: [UtilsUnit.remToPx(3), UtilsUnit.remToPx(3)],
    iconAnchor: [UtilsUnit.remToPx(1.5), UtilsUnit.remToPx(1.5)],
  });

  const mapMarkers = markers.filter(
    (marker) => (marker.latitude && marker.longitude) || MapPolygonUtils.parseTerminalGeoJSON(marker)
  );

  function polygon_ui(marker) {
    const polygonGeoJson = MapPolygonUtils.parseTerminalGeoJSON(marker);
    if (!polygonGeoJson?.coordinates) return null;

    return polygonGeoJson.coordinates.map((polygon, index) => {
      const centerPt = MapPolygonUtils.getPolygonCenter(polygon);
      const positions = polygon[0].map(([lng, lat]) => [lat, lng]);

      return (
        <Polygon
          key={`${marker.terminalID}-polygon-${index}`}
          positions={positions}
          pathOptions={ConstantsMap.getTerminalPolygonPathOptions(mapType)}
          eventHandlers={{
            click: (e) => {
              if (e.originalEvent) e.originalEvent.stopPropagation();
              handleMarkerClick({
                ...marker,
                latitude: centerPt?.lat ?? marker.latitude,
                longitude: centerPt?.lng ?? marker.longitude,
              });
            },
          }}
        />
      );
    });
  }

  function marker_ui(marker) {
    if (!marker.latitude || !marker.longitude) return null;

    return (
      <Marker
        key={marker.terminalID}
        position={[marker.latitude, marker.longitude]}
        icon={busIcon}
        eventHandlers={{
          click: (e) => {
            if (e.originalEvent) e.originalEvent.stopPropagation();
            handleMarkerClick(marker);
          },
        }}
      />
    );
  }

  function mapFeatures_ui(marker) {
    return (
      <React.Fragment key={marker.terminalID}>
        {polygon_ui(marker)}
        {showPinMarkers && marker_ui(marker)}
      </React.Fragment>
    );
    
  }

  function info_window_ui() {
    if (!selectedMarker) return null;
    return (
      <DivColumn className="info-window-frame-1">
        <DivRow className="info-window-frame-2">
          <DivColumn className="info-window-frame-3">
            <ParagraphText className="info-window-title-1">{selectedMarker.terminalName}</ParagraphText>
            <ParagraphText className="info-window-title-2">{selectedMarker.erezSKU}</ParagraphText>
          </DivColumn>
          <div className="widget-markers-map-popup-nav-button" style={{ cursor: "pointer" }}>
            <WidgetIcon
              iconProp={ConstantsImages.LEFT_ARROW_IMAGE}
              backgroundColorProp={"#5A5A5A"}
              heightProp="3.5rem"
              widthIconProp="1.7rem"
              heightIconProp="1.1rem"
              handleOnClickProp={(e) => {
                e.stopPropagation();
                UtilsUnit.handleSearchFrameClick(navigate, selectedMarker);
              }}
            />
          </div>
        </DivRow>
      </DivColumn>
    );
  }

  return (
    <WidgetMapLayout
      containerRef={mapContainerRef}
      className="widget-markers-map"
      style={{ height: heightProp, width: widthProp, position: "relative", transition: transitionProp }}
      onClick={handleCloseMarkerClick}
      center={center}
      zoom={zoom}
      setMapRef={setMapRef}
      onMapStateChange={setMapState}
      mapType={mapType}
      setMapType={setMapType}
    >
      {mapMarkers.map((marker) => mapFeatures_ui(marker))}
      {selectedMarker && (
        <>
          <SelectedMarkerPopup selectedMarker={selectedMarker} contentRef={popupContentRef} navigate={navigate} />
          <div ref={popupContentRef} className="widget-markers-map-popup-content" style={{ display: "none" }}>
            {info_window_ui()}
          </div>
        </>
      )}
    </WidgetMapLayout>
  );
}

export default React.memo(WidgetMarkersMap);
